import {
  ChangeDetectionStrategy,
  Component,
  inject,
  input,
  OnDestroy,
  OnInit,
  output,
  signal,
  viewChild,
} from '@angular/core';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { CodeInputComponent, CodeInputModule } from 'angular-code-input';
import { interval, Subscription, takeWhile, tap } from 'rxjs';
import { OTPChannelType } from '../../model/auth-channels';
import { OtpDetails, OtpService } from '../../services/otp/otp.service';
import { WebConfigService } from '../../services/web-config/web-config.service';

export const SALES_EMAIL = 'sales@halodot.io';
const TIMEOUT_IN_SECONDS = 120;
export interface OtpLoad {
  value: string;
  otp: string;
  id: string;
}

@Component({
  selector: 'app-otp',
  imports: [TranslatePipe, CodeInputModule],
  templateUrl: './otp.component.html',
  styleUrls: ['./otp.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class OtpComponent implements OnInit, OnDestroy {
  #otpService = inject(OtpService);
  #webconfig = inject(WebConfigService);
  #translate = inject(TranslateService);
  verifyGUID = signal<string | null>(null);
  timeLeft = signal<number>(TIMEOUT_IN_SECONDS);
  otpNumber = viewChild<CodeInputComponent>('otpNumber');
  channelValue = input<string>();
  channelType = input<OTPChannelType>(OTPChannelType.Email);
  webBackend = input<boolean>(true);
  goBack = output();
  verified = output<OtpLoad>();
  displayMessage = signal('');
  isCodeHidden = input<boolean>(true);
  #timerSub?: Subscription;
  #verified = false;

  ngOnInit(): void {
    this.displayMessage.set(this.#translate.instant('MESSAGES.PROMPTS.ENTER_CODE_SENT_TO_YOU'));
    this.getOtp();
  }

  ngOnDestroy(): void {
    this.stopTimer();
  }

  getOtp(): void {
    if (!this.channelValue()) {
      return;
    }
    const otpDetails: OtpDetails = {
      channel: this.channelType(),
      address: this.channelValue(),
      apkPackageName: this.#webconfig.apkPackageName,
    };
    this.#otpService.sendOtp(otpDetails).subscribe((id) => {
      this.startTimer();
      this.verifyGUID.set(id as string);
    });
  }

  verifyOtp(code: string): void {
    if (this.#verified || code.length < 4) {
      return;
    }
    this.#verified = true;
    this.#otpService
      .verifyOtp(this.channelValue() as string, code, this.verifyGUID() as string, this.channelType())
      .subscribe({
        next: () => {
          this.verified.emit({ id: this.verifyGUID() as string, otp: code, value: this.channelValue() });
          this.resetTimer();
        },
        complete: () => this.otpNumber()?.reset(),
      });
  }

  startTimer() {
    this.stopTimer();
    this.timeLeft.set(TIMEOUT_IN_SECONDS);
    this.#timerSub = interval(1000)
      .pipe(
        tap(() => this.timeLeft.update((time) => time - 1)),
        takeWhile((time) => time < TIMEOUT_IN_SECONDS),
      )
      .subscribe({
        complete: () => this.resetTimer(),
      });
  }

  stopTimer() {
    if (this.#timerSub) {
      this.#timerSub.unsubscribe();
    }
  }

  resetTimer() {
    this.stopTimer();
    this.timeLeft.set(TIMEOUT_IN_SECONDS);
    this.verifyGUID.set(null);
    this.otpNumber()?.reset();
  }
}
